/**
 * Sidebar - main shell with header, nav categories, user menu.
 * Supports collapsed/expanded states with smooth animation.
 * On mobile (< 900px) renders as a temporary Drawer.
 */

import React, { useMemo } from 'react';
import { styled, useTheme } from '@mui/material/styles';
import MuiDrawer from '@mui/material/Drawer';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useAuth } from '../../Context/AuthenticationContext';
import { hasAccess } from '../../Utils/roles';
import { navConfig } from './navConfig';
import { useSidebarCollapsed } from './useSidebarCollapsed';
import SidebarHeader from './SidebarHeader';
import SidebarCollapse from './SidebarCollapse';
import SidebarCategory from './SidebarCategory';
import SidebarUserMenu from './SidebarUserMenu';

const EXPANDED_WIDTH = 220;
const COLLAPSED_WIDTH = 64;
const MOBILE_BP = 900;
const EASE = 'cubic-bezier(0.32, 0.72, 0, 1)';

const SidebarRoot = styled('nav')<{ collapsed: boolean }>(({ theme, collapsed }) => ({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
  backgroundColor: theme.tokens.sidebarBg,
  borderRight: `1px solid ${theme.tokens.sidebarBorder}`,
  transition: `width 220ms ${EASE}`,
  overflow: 'hidden',
}));

const NavScroll = styled('div')({
  flex: 1,
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingTop: 8,
  paddingBottom: 8,
});

interface SidebarInternalProps {
  collapsed: boolean;
  onToggle: () => void;
  showCollapse?: boolean;
}

function SidebarContent({ collapsed, onToggle, showCollapse = true }: SidebarInternalProps) {
  const { user } = useAuth();
  const roles = useMemo(() => user?.roles ?? [], [user?.roles]);

  const filteredCategories = useMemo(
    () =>
      navConfig.map(cat => ({
        ...cat,
        filteredItems: cat.items
          .filter(item => item.show !== false)
          .map(item => ({ ...item, locked: !hasAccess(roles, item.roles) })),
      })),
    [roles]
  );

  return (
    <SidebarRoot collapsed={collapsed}>
      <SidebarHeader collapsed={collapsed} />

      <NavScroll>
        {filteredCategories.map(cat => {
          if (cat.filteredItems.length === 0) return null;
          return (
            <SidebarCategory
              key={cat.id}
              category={cat}
              items={cat.filteredItems}
              collapsed={collapsed}
            />
          );
        })}
      </NavScroll>

      {showCollapse && <SidebarCollapse collapsed={collapsed} onToggle={onToggle} />}

      <SidebarUserMenu collapsed={collapsed} />
    </SidebarRoot>
  );
}

export interface SidebarProps {
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export function Sidebar({ mobileOpen, onMobileClose }: SidebarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(MOBILE_BP));
  const { collapsed, toggle } = useSidebarCollapsed();

  if (isMobile) {
    return (
      <MuiDrawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }}
        sx={{
          '& .MuiDrawer-paper': {
            width: EXPANDED_WIDTH,
            boxSizing: 'border-box',
            borderRight: `1px solid ${theme.tokens.sidebarBorder}`,
          },
        }}
      >
        <SidebarContent collapsed={false} onToggle={onMobileClose} showCollapse={false} />
      </MuiDrawer>
    );
  }

  return (
    <MuiDrawer
      variant="permanent"
      open
      sx={{
        width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
        flexShrink: 0,
        transition: `width 220ms ${EASE}`,
        '& .MuiDrawer-paper': {
          width: collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH,
          boxSizing: 'border-box',
          border: 'none',
          overflow: 'hidden',
          transition: `width 220ms ${EASE}`,
        },
      }}
    >
      <SidebarContent collapsed={collapsed} onToggle={toggle} />
    </MuiDrawer>
  );
}

export { EXPANDED_WIDTH, COLLAPSED_WIDTH, MOBILE_BP };
